/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ComponentProps, ComponentType, ReactNode } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { If, Then, Else } from "react-if";
import { ErrorMessage, DefaultErrorFallback } from "~/components/Error";
import { noop } from "~/tools";

type ErrorMessageProps = React.ComponentProps<typeof ErrorMessage>;

type Props = ErrorMessageProps & {
  children?: ReactNode;
  Component?: ComponentType<ErrorMessageProps>;
  fallback?: ComponentType<FallbackProps>;
  onReset?: ComponentProps<typeof ErrorBoundary>["onReset"];
  resetKeys?: ComponentProps<typeof ErrorBoundary>["resetKeys"];
};

const defaultResetKeys: unknown[] = [];

export function ErrorWrap({
  Component = DefaultComponent,
  fallback = DefaultErrorFallback,
  onReset = noop,
  resetKeys = defaultResetKeys,
  ...props
}: Props) {
  return (
    <ErrorBoundary FallbackComponent={fallback} resetKeys={resetKeys} onError={noop} onReset={onReset}>
      <If condition={props.error != null}>
        <Then>
          <Component {...props} />
        </Then>
        <Else>{props.children}</Else>
      </If>
    </ErrorBoundary>
  );
}

type DefaultComponentProps = Omit<Props, "Component" | "fallback" | "onReset">;

function DefaultComponent({ children, ...props }: DefaultComponentProps) {
  return (
    <>
      <ErrorMessage {...props} />
      {children}
    </>
  );
}
